import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BrandBadge } from "@/components/Logo";
import { Mail, MapPin, Phone, Instagram, Facebook, Youtube, Twitter, ArrowRight, ShieldCheck, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/components/SiteSettings";
import { useHeaderNav } from "@/hooks/useHeaderNav";
import { useFooterLinks } from "@/hooks/useFooterLinks";

export default function Footer({ tight = false }) {
  const settings = useSiteSettings();
  const { isHrefVisible } = useHeaderNav();
  const { columns: footerCols } = useFooterLinks();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/newsletter/subscribe", { email, source: "footer" });
      if (res.data?.already_subscribed) {
        toast.success("You're already on the list — thanks again!");
      } else {
        toast.success("Subscribed! Check your inbox for WELCOME10 — 10% OFF.");
      }
      setEmail("");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : "Couldn't subscribe — please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className={`bg-zinc-100 text-zinc-700 border-t border-zinc-200 ${tight ? "" : "mt-12 md:mt-20"}`}>
      {/* Newsletter band — soft ash accent */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5722]">Get 10% off your first order</div>
            <div className="mt-2 font-display font-extrabold text-2xl md:text-3xl tracking-tight leading-tight text-[#111111]">
              Join the XYZPLUS club
            </div>
            <p className="mt-1 text-sm text-zinc-600 max-w-md">Bulk deals, new arrivals, design inspiration — straight to your inbox.</p>
          </div>
          <form onSubmit={submitNewsletter} className="md:col-span-5 flex items-stretch gap-2" data-testid="footer-newsletter">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="footer-newsletter-input"
              className="flex-1 min-w-0 bg-white border border-zinc-300 focus:border-[#FF5722] px-4 h-12 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none transition-colors rounded-xl"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="bg-[#FF5722] hover:bg-orange-600 text-white font-bold px-5 h-12 inline-flex items-center gap-2 text-sm uppercase tracking-wider transition-colors whitespace-nowrap disabled:opacity-60 rounded-xl"
            >
              {busy ? "..." : "Subscribe"} <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>

      {/* Main grid — dense 5-col layout (LIGHT ASH theme) */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14 grid grid-cols-2 md:grid-cols-12 gap-y-8 gap-x-6 md:gap-8">
        {/* Brand column */}
        <div className="col-span-2 md:col-span-4" data-testid="footer-col-1">
          <BrandBadge size={96} />
          <p className="mt-4 text-zinc-600 text-sm max-w-sm leading-relaxed">
            India&apos;s hybrid platform for custom apparel + ready-made merchandise.
            From a single tee to 10,000 corporate gifts.
          </p>
          <div className="mt-5 space-y-2 text-xs text-zinc-600">
            {settings.business_phone && (
              <a href={`tel:${settings.business_phone}`} className="flex items-center gap-2 hover:text-[#FF5722]">
                <Phone size={12} /> {settings.business_phone}
              </a>
            )}
            {settings.business_email && (
              <a href={`mailto:${settings.business_email}`} className="flex items-center gap-2 hover:text-[#FF5722]">
                <Mail size={12} /> {settings.business_email}
              </a>
            )}
            {settings.business_address && (
              <div className="flex items-start gap-2"><MapPin size={12} className="mt-0.5 flex-shrink-0" /> <span className="line-clamp-2">{settings.business_address}</span></div>
            )}
          </div>
          {/* Social — only show icons that have a URL configured. WhatsApp
              accepts either a raw phone number (auto-converted to wa.me) or a
              full URL, so admins don't have to remember the wa.me syntax. */}
          <div className="mt-5 flex items-center gap-3 text-zinc-600">
            {(() => {
              const wa = (settings.social_whatsapp || "").trim();
              let waHref = null;
              if (wa) {
                if (/^https?:\/\//i.test(wa)) {
                  waHref = wa;
                } else {
                  // Strip anything that's not a digit and prefix with wa.me/.
                  // Phone numbers can be entered as +91 98765 43210,
                  // 91-98765-43210, etc — all normalise to the same link.
                  const digits = wa.replace(/\D+/g, "");
                  if (digits.length >= 8) waHref = `https://wa.me/${digits}`;
                }
              }
              const links = [
                { Icon: Instagram, href: settings.social_instagram, label: "Instagram" },
                { Icon: Facebook, href: settings.social_facebook, label: "Facebook" },
                { Icon: MessageCircle, href: waHref, label: "WhatsApp", testId: "footer-social-whatsapp" },
                { Icon: Twitter, href: settings.social_twitter, label: "Twitter" },
                { Icon: Youtube, href: settings.social_youtube, label: "YouTube" },
              ].filter(s => s.href && s.href !== "#");
              return links.map(({ Icon, href, label, testId }, i) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  className="w-9 h-9 bg-white border border-zinc-200 hover:border-[#FF5722] hover:text-[#FF5722] flex items-center justify-center transition-colors rounded-full"
                  data-testid={testId || `footer-social-${i}`}
                >
                  <Icon size={14} />
                </a>
              ));
            })()}
          </div>
        </div>

        {/* Shop — header-nav-managed hrefs auto-sync via useHeaderNav. The
            extra footer-only links (New Arrivals, Best Sellers, Custom Design
            Lab etc) now live in the footer_links CMS so admins can add /
            rename / hide them without a code deploy. */}
        <div className="md:col-span-2" data-testid="footer-col-2">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5722] mb-4">Shop</h4>
          <ul className="space-y-2.5 text-sm text-zinc-600">
            {isHrefVisible("/shop") && (
              <li><Link to="/shop" className="hover:text-[#FF5722]" data-testid="footer-shop">All Products</Link></li>
            )}
            {footerCols.shop.map((l) => (
              <li key={l.id}>
                <FooterLink link={l} testIdPrefix="footer-cms-shop" />
              </li>
            ))}
            {isHrefVisible("/custom-shop") && (
              <li><Link to="/custom-shop" className="hover:text-[#FF5722]" data-testid="footer-custom-shop">Custom Shop</Link></li>
            )}
            {isHrefVisible("/couple-wear") && (
              <li><Link to="/couple-wear" className="hover:text-[#FF5722]" data-testid="footer-couple-wear">Couple Wear</Link></li>
            )}
            {isHrefVisible("/heavy-duty") && (
              <li><Link to="/heavy-duty" className="hover:text-[#FF5722]" data-testid="footer-heavy-duty">Heavy Duty</Link></li>
            )}
            {isHrefVisible("/sell-your-designs") && (
              <li><Link to="/sell-your-designs" className="hover:text-[#FF5722]" data-testid="footer-sell-designs">Sell Your Designs</Link></li>
            )}
            {isHrefVisible("/leaderboard") && (
              <li><Link to="/leaderboard" className="hover:text-[#FF5722]" data-testid="footer-leaderboard">Designer Leaderboard</Link></li>
            )}
            <li>
              <Link
                to="/try-on"
                className="inline-flex items-center gap-1.5 font-bold text-[#FF5722] hover:underline"
                data-testid="footer-tryon"
              >
                AI Try-On
                <span className="text-[8px] uppercase tracking-wider font-black bg-[#FF5722] text-white px-1 py-px rounded">New</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Collections — fully admin-managed via /admin/footer-settings */}
        <div className="md:col-span-2" data-testid="footer-col-3">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5722] mb-4">Collections</h4>
          <ul className="space-y-2.5 text-sm text-zinc-600">
            {footerCols.collections.map((l) => (
              <li key={l.id}><FooterLink link={l} testIdPrefix="footer-cms-collections" /></li>
            ))}
          </ul>
        </div>

        {/* Support — fully admin-managed via /admin/footer-settings */}
        <div className="md:col-span-2" data-testid="footer-col-4">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5722] mb-4">Support</h4>
          <ul className="space-y-2.5 text-sm text-zinc-600">
            {footerCols.support.map((l) => (
              <li key={l.id}>
                <FooterLink link={l} testIdPrefix="footer-cms-support" highlight={/refer/i.test(l.href || "")} />
              </li>
            ))}
          </ul>
        </div>

        {/* Company — fully admin-managed via /admin/footer-settings.
            Legal team can revise the visible link set without a code deploy. */}
        <div className="md:col-span-2" data-testid="footer-col-5">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5722] mb-4">Company</h4>
          <ul className="space-y-2.5 text-sm text-zinc-600">
            {footerCols.company.map((l) => (
              <li key={l.id}><FooterLink link={l} testIdPrefix="footer-cms-company" /></li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom strip — copyright + payment icons + trust */}
      <div className="border-t border-zinc-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-[11px] text-zinc-500">
            © 2026 XYZPLUS Apparel · Made in India 🇮🇳
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-600">
              <ShieldCheck size={12} className="text-[#FF5722]" /> Razorpay Secure
            </div>
            <div className="flex items-center gap-2">
              {/* Payment method chips — text-based to avoid image dependencies */}
              {["VISA", "MC", "RUPAY", "UPI", "NB"].map((m) => (
                <span key={m} className="text-[9px] font-extrabold tracking-wider text-zinc-700 bg-white border border-zinc-300 px-2 py-1 rounded-md">
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Render a single footer-cms link. Generates a stable data-testid from the
 * row id so e2e tests can grab any link by `[data-testid="footer-cms-{col}-{id}"]`.
 * Internal links use react-router <Link>; external ones (http*) use a normal
 * <a> with `target="_blank"` so accidental admin URLs don't break SPA routing.
 */
function FooterLink({ link, testIdPrefix, highlight = false }) {
  const isExternal = /^https?:\/\//i.test(link.href || "");
  const cls = `hover:text-[#FF5722]${highlight ? " font-semibold" : ""}`;
  const testId = `${testIdPrefix}-${link.id}`;
  if (isExternal || link.open_in_new_tab) {
    return (
      <a
        href={link.href}
        target={link.open_in_new_tab || isExternal ? "_blank" : undefined}
        rel="noreferrer"
        className={cls}
        data-testid={testId}
      >
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.href} className={cls} data-testid={testId}>
      {link.label}
    </Link>
  );
}
